import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../common/Card.jsx';
import ClientSelectDropdown from '../common/ClientSelectDropdown.jsx';
import LocalSelectDropdown from '../common/LocalSelectDropdown.jsx';
import dashboardService from '../../services/dashboardService';
import clientService from '../../services/clientService';
import localService from '../../services/localService';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [headerStats, setHeaderStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Separate states for each chart
  const [articlesData, setArticlesData] = useState(null);
  const [lastIntervention, setLastIntervention] = useState(null);
  const [barChartLoading, setBarChartLoading] = useState(false);
  const [circleChartLoading, setCircleChartLoading] = useState(false);
  
  // Separate client selection for bar chart (only for admin)
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedBarChartClientId, setSelectedBarChartClientId] = useState('');
  
  // Add client selection for circle chart
  const [selectedCircleChartClientId, setSelectedCircleChartClientId] = useState('');
  
  // Local selection for circle chart (only for admin)
  const [locals, setLocals] = useState([]);
  const [localsLoading, setLocalsLoading] = useState(false);
  const [selectedCircleChartLocalId, setSelectedCircleChartLocalId] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  // Effect for bar chart client selection (admin) or load client data immediately (non-admin)
  useEffect(() => {
    if (user?.isAdmin) {
      console.log('this is the user', user);
      // Admin: load based on client selection
      if (selectedBarChartClientId) {
        loadBarChartData(selectedBarChartClientId);
      } else {
        setArticlesData(null);
      }
    } else {
      console.log('this is the user', user);
      if (user?.idUser) {
        loadBarChartData(user.idUser); // Use idUser as clientId for non-admin
      }
    }
  }, [selectedBarChartClientId, user]);

  // Effect for circle chart local selection (admin) or load local data immediately (non-admin)
  useEffect(() => {
    if (user?.isAdmin) {
      // Admin: load based on local selection
      if (selectedCircleChartLocalId) {
        loadCircleChartData(selectedCircleChartLocalId);
      } else {
        setLastIntervention(null);
      }
    } else {
          if (selectedCircleChartLocalId) {
        loadCircleChartData(selectedCircleChartLocalId);
      } else {
        setLastIntervention(null);
      }
      // Client: load their own data using idUser as localId
      // if (user?.idUser) {
      //   loadCircleChartData(user.idUser); // Use idUser as localId for non-admin
      // }
    }
  }, [selectedCircleChartLocalId, selectedCircleChartClientId, user]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load header stats
      const headerRes = await dashboardService.getHeaderStats();
      if (headerRes.success) {
        setHeaderStats(headerRes.data);
      }

      // Load clients for bar chart dropdown and locals for circle chart dropdown (only for admin)
      if (user?.isAdmin) {
        await loadClients(); // Keep this for bar chart
        // Add this for circle chart
      }
       await loadLocals();
    } catch (error) {
      console.error('Dashboard loading error:', error);
      toast.error('Erreur lors du chargement du dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Load clients for admin
  const loadClients = async () => {
    setClientsLoading(true);
    try {
      const response = await clientService.getClients(1000, 1); // Get all clients
      if (response.success) {
        const clientList = Array.isArray(response.data?.list) 
          ? response.data.list 
          : Array.isArray(response.data) 
            ? response.data 
            : [];
        console.log('Loaded clients:', clientList);
        setClients(clientList);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setClientsLoading(false);
    }
  };

  // Add loadLocals function
  const loadLocals = async () => {
    setLocalsLoading(true);
    try {
      const response = await localService.getLocaux(1000, 1); // Get all locals
      if (response.success) {
        const localList = Array.isArray(response.data?.list) 
          ? response.data.list 
          : Array.isArray(response.data) 
            ? response.data 
            : [];
        console.log('Loaded locals:', localList);
        setLocals(localList);
      }
    } catch (error) {
      console.error('Error loading locals:', error);
      toast.error('Erreur lors du chargement des locaux');
    } finally {
      setLocalsLoading(false);
    }
  };

  const loadBarChartData = async (clientIdOrUserId) => {
    if (!clientIdOrUserId) return;
    
    setBarChartLoading(true);
    try {
      // For admin: pass clientId, for client: pass their idUser as clientId
      const articlesRes = user?.isAdmin 
        ? await dashboardService.getDashboardArticles(clientIdOrUserId) // Admin passes selected clientId
        : await dashboardService.getDashboardArticles(clientIdOrUserId); // Client passes their idUser as clientId
      
      if (articlesRes.success) {
        setArticlesData(articlesRes.data);
      } else {
        console.error('Articles API error:', articlesRes.message);
        setArticlesData(null);
        toast.error('Erreur lors du chargement des données articles');
      }
    } catch (error) {
      console.error('Bar chart loading error:', error);
      toast.error('Erreur lors du chargement des données articles');
      setArticlesData(null);
    } finally {
      setBarChartLoading(false);
    }
  };

  const loadCircleChartData = async (localIdOrUserId) => {
    if (!localIdOrUserId) return;
    
    setCircleChartLoading(true);
    try {
      // For admin: pass localId, for client: pass their idUser as localId
      const interventionRes = user?.isAdmin 
        ? await dashboardService.getLastIntervention(localIdOrUserId) // Admin passes selected localId
        : await dashboardService.getLastIntervention(localIdOrUserId); // Client passes their idUser as localId
      
      if (interventionRes.success) {
        setLastIntervention(interventionRes.data);
      } else {
        console.error('Intervention API error:', interventionRes.message);
        setLastIntervention(null);
        toast.error('Erreur lors du chargement des données intervention');
      }
    } catch (error) {
      console.error('Circle chart loading error:', error);
      toast.error('Erreur lors du chargement des données intervention');
      setLastIntervention(null);
    } finally {
      setCircleChartLoading(false);
    }
  };

  // Generate bar chart data
  const generateBarChartData = () => {
    if (!articlesData || articlesData.length === 0) return null;

    const currentYear = new Date().getFullYear();
    const labels = articlesData.map(item => item.year.toString());
    const data = articlesData.map(item => item.count);
    const backgroundColors = articlesData.map(item => {
      const isOld = item.year < (currentYear - 10);
      return isOld 
        ? (isDarkMode ? '#ef4444' : '#dc2626') // Red for old
        : (isDarkMode ? '#10b981' : '#059669'); // Green for recent
    });

    return {
      labels,
      datasets: [
        {
          label: 'Teste de reépreuve ou renouvellement des extincteurs',
          data,
          backgroundColor: backgroundColors,
          borderColor: backgroundColors,
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };
  };

 const generateCircleChartData = () => {
  if (!lastIntervention?.startDate) return null;

  const currentDate = new Date();
  const startDate = new Date(lastIntervention.startDate);
  const isAnnuelle = lastIntervention.client?.annuelle === true;

  const isDark = isDarkMode;

  const green  = isDark ? '#10b981' : '#059669';
  const yellow = isDark ? '#f59e0b' : '#d97706';
  const red    = isDark ? '#ef4444' : '#dc2626';

  const monthNames = [
    '', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
    'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'
  ];

  const normalizeMonth = (m) => ((m - 1) % 12) + 1;

  /* ==========================================================
     ANNUAL MODE
  ========================================================== */
  if (isAnnuelle) {
    const currentYear = currentDate.getFullYear();
    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth() + 1;
    const currentMonth = currentDate.getMonth() + 1;

    let color = green;
    let label = currentYear.toString();

    if (currentYear === startYear) {
      color = green;
      label = currentYear.toString();
    } else if (currentYear - startYear === 1 && currentMonth > startMonth) {
      color = yellow;
      label = `${currentYear} (${startMonth})`;
    } else {
      color = red;
      label = `${startYear} (${startMonth})`;
    }

    return {
      labels: [label],
      datasets: [{
        data: [100],
        backgroundColor: [color],
        borderWidth: 2,
        borderColor: isDark ? '#374151' : '#ffffff',
      }],
      centerText: label,
    };
  }

  /* ==========================================================
     SEMI-ANNUAL MODE
  ========================================================== */

  const currentYear = currentDate.getFullYear();
  const startYear = startDate.getFullYear();

  const currentMonth = currentDate.getMonth() + 1;
  const startMonth = startDate.getMonth() + 1;

  const secondPeriodMonth = normalizeMonth(startMonth + 6);

  const rightLabel = monthNames[startMonth];
  const leftLabel = monthNames[secondPeriodMonth];

  let rightColor = green;
  let leftColor = green;

  // Same year
  if (currentYear === startYear) {
    if (currentMonth <= secondPeriodMonth) {
      rightColor = green;
      leftColor = yellow;
    } else {
      rightColor = green;
      leftColor = red;
    }
  }
  // More than 1 year passed
  else if (currentYear - startYear > 1) {
    rightColor = red;
    leftColor = red;
  }
  // Exactly 1 year difference
  else {
    if (currentMonth <= secondPeriodMonth) {
      rightColor = yellow;
      leftColor = yellow;
    } else {
      rightColor = yellow;
      leftColor = red;
    }
  }

  return {
    labels: [leftLabel, rightLabel],
    datasets: [{
      data: [50, 50],
      backgroundColor: [leftColor, rightColor],
      borderWidth: 2,
      borderColor: isDark ? '#374151' : '#ffffff',
    }],
    centerText: `${leftLabel}/${rightLabel}`,
  };
};

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
        titleColor: isDarkMode ? '#ffffff' : '#000000',
        bodyColor: isDarkMode ? '#ffffff' : '#000000',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          color: isDarkMode ? '#374151' : '#f3f4f6',
        },
        ticks: {
          color: isDarkMode ? '#9ca3af' : '#6b7280',
        },
      },
      y: {
        grid: {
          color: isDarkMode ? '#374151' : '#f3f4f6',
        },
        ticks: {
          color: isDarkMode ? '#9ca3af' : '#6b7280',
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        display: true, // Enable legend to show labels
        position: 'bottom',
        labels: {
          color: isDarkMode ? '#ffffff' : '#000000',
          font: {
            size: 12,
            weight: 'bold',
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        enabled: true, // Enable tooltips
        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
        titleColor: isDarkMode ? '#ffffff' : '#000000',
        bodyColor: isDarkMode ? '#ffffff' : '#000000',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const isAnnuelle = lastIntervention?.client?.annuelle;
            if (isAnnuelle) {
              return `Année: ${label}`;
            } else {
              return `Mois: ${label}`;
            }
          }
        }
      },
    },
  };

  // Enhanced center text plugin for doughnut chart
  const centerTextPlugin = {
    id: 'centerText',
    beforeDraw: (chart) => {
      if (chart.config.data.centerText) {
        const { ctx, width, height } = chart;
        ctx.restore();
        const fontSize = Math.min(width, height) / 10;
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = isDarkMode ? '#ffffff' : '#000000';
        ctx.fillText(chart.config.data.centerText, width / 2, height / 2);
        ctx.save();
      }
    },
    afterDraw: (chart) => {
      // Add labels on segments for better visibility
      const { ctx, data } = chart;
      const meta = chart.getDatasetMeta(0);
      
      if (data.labels && meta.data.length > 1) {
        // Only for semi-annual (2 segments)
        meta.data.forEach((segment, index) => {
          const { x, y } = segment.tooltipPosition();
          ctx.save();
          ctx.font = 'bold 14px Arial';
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(data.labels[index], x, y);
          ctx.restore();
        });
      }
    },
  };

  // Empty state component for charts
  const EmptyChartState = ({ title, icon, description }) => (
    <div className="h-80 flex flex-col items-center justify-center">
      <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
        isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
      }`}>
        <i className={`${icon} text-2xl`}></i>
      </div>
      <h4 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        {title}
      </h4>
      <p className={`text-sm text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
        {description}
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`}></div>
          <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  const barChartData = generateBarChartData();
  const circleChartData = generateCircleChartData();
  const selectedBarClient = clients.find(c => String(c.id) === String(selectedBarChartClientId));
  const selectedCircleLocal = locals.find(l => String(l.id) === String(selectedCircleChartLocalId));

  return (
    <div className={`min-h-screen p-4 sm:p-6 lg:p-8 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-3xl sm:text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Dashboard
            </h1>
            <p className={`text-lg mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Vue d'ensemble de votre activité
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={loadInitialData}
              className={`px-6 py-3 rounded-xl font-medium transition-all transform hover:scale-105 ${
                isDarkMode 
                  ? 'bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              } shadow-lg`}
            >
              <i className="fas fa-sync-alt mr-2"></i>
              Actualiser
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        {headerStats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Total Articles',
                value: headerStats.totalArticles?.toLocaleString() || '0',
                icon: 'fas fa-fire-extinguisher',
                gradient: 'from-blue-500 to-blue-600',
                bgGradient: 'from-blue-50 to-blue-100',
                iconBg: 'bg-blue-500',
              },
              {
                title: 'Total Locaux',
                value: headerStats.totalLocals?.toLocaleString() || '0',
                icon: 'fas fa-building',
                gradient: 'from-green-500 to-green-600',
                bgGradient: 'from-green-50 to-green-100',
                iconBg: 'bg-green-500',
              },
              {
                title: 'Certificats',
                value: headerStats.totalCertif?.toLocaleString() || '0',
                icon: 'fas fa-certificate',
                gradient: 'from-yellow-500 to-yellow-600',
                bgGradient: 'from-yellow-50 to-yellow-100',
                iconBg: 'bg-yellow-500',
              },
              {
                title: 'Interventions',
                value: headerStats.totalInterventions?.toLocaleString() || '0',
                icon: 'fas fa-tools',
                gradient: 'from-purple-500 to-purple-600',
                bgGradient: 'from-purple-50 to-purple-100',
                iconBg: 'bg-purple-500',
              },
            ].map((stat, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden transform hover:scale-105 transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 shadow-xl' 
                    : `bg-gradient-to-br ${stat.bgGradient} border-gray-200 shadow-xl`
                }`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {stat.title}
                      </p>
                      <p className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {stat.value}
                      </p>
                    </div>
                    <div className={`p-4 rounded-2xl ${stat.iconBg} shadow-lg`}>
                      <i className={`${stat.icon} text-2xl text-white`}></i>
                    </div>
                  </div>
                </div>
                {/* Decorative gradient overlay */}
                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${stat.gradient} opacity-10 rounded-full transform translate-x-8 -translate-y-8`}></div>
              </Card>
            ))}
          </div>
        )}

        {/* Charts Section - For both admin and clients */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Bar Chart */}
          <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} shadow-xl`}>
            <div className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <i className="fas fa-chart-bar text-primary-500"></i>
                  </div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Teste de reépreuve ou renouvellement des extincteurs
                  </h3>
                </div>
                {/* Only show client dropdown for admin */}
                {user?.isAdmin && (
                  <div className={`relative w-full sm:w-64 ${isDarkMode ? 'bg-gray-700' : 'bg-white'} rounded-xl shadow-md border ${isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'} transition-colors`}>
                    <ClientSelectDropdown
                      value={selectedBarChartClientId}
                      onChange={setSelectedBarChartClientId}
                      options={clients}
                      loading={clientsLoading}
                      disabled={clientsLoading}
                      isDarkMode={isDarkMode}
                      placeholder="Sélectionner un client..."
                      required={false}
                      className={`w-full p-3 rounded-xl focus:outline-none ${isDarkMode ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'} ${clientsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                  </div>
                )}
              </div>

              {/* Show client info for admin, user info for client */}
              {user?.isAdmin && selectedBarClient && (
                <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <strong>Client:</strong> {selectedBarClient.raisonSocial}
                  </p>
                </div>
              )}

              {!user?.isAdmin && (
                <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <strong>Vos données:</strong> Articles par année
                  </p>
                </div>
              )}
              
              {barChartLoading ? (
                <div className="h-80 flex items-center justify-center">
                  <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`}></div>
                </div>
              ) : barChartData ? (
                <>
                  <div className="h-80">
                    <Bar data={barChartData} options={chartOptions} />
                  </div>
                  <div className="flex items-center justify-center mt-4 space-x-6">
                    
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Ancien (plus que 10 ans)
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Récent (≤10 ans)
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <EmptyChartState
                  title="Aucune donnée"
                  icon="fas fa-chart-bar"
                  description={user?.isAdmin && !selectedBarChartClientId ? "Sélectionnez un client pour voir les données" : "Aucun article trouvé"}
                />
              )}
            </div>
          </Card>

          {/* Circle Chart */}
          <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'} shadow-xl`}>
            <div className="p-6">
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <i className="fas fa-chart-pie text-primary-500"></i>
                  </div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Taux de respect date des contrôles réglementaires
                  </h3>
                </div>
                {/* Show client and local dropdowns for admin */}
               
                  <div className="flex flex-col lg:flex-row items-start gap-3 mt-4 sm:mt-0">
                 {user?.isAdmin && (
                    <div className={`relative w-full lg:w-52 ${isDarkMode ? 'bg-gray-700' : 'bg-white'} rounded-xl shadow-md border ${isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'} transition-colors`}>
                      <ClientSelectDropdown
                        value={selectedCircleChartClientId}
                        onChange={(value) => {
                          setSelectedCircleChartClientId(value);
                          setSelectedCircleChartLocalId(''); // Reset local selection when client changes
                        }}
                        options={clients}
                        loading={clientsLoading}
                        disabled={clientsLoading}
                        isDarkMode={isDarkMode}
                        placeholder="Sélectionner un client..."
                        required={false}
                        className={`w-full p-3 rounded-xl focus:outline-none ${isDarkMode ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'}`}
                      />
                    </div>
                           )}
                    <div className={`relative w-full lg:w-52 ${isDarkMode ? 'bg-gray-700' : 'bg-white'} rounded-xl shadow-md border ${isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'} transition-colors`}>
                      <LocalSelectDropdown
                        value={selectedCircleChartLocalId}
                        onChange={setSelectedCircleChartLocalId}
                        options={locals.filter(local => {
                          return selectedCircleChartClientId ? 
                            (String(local.clientId) === String(selectedCircleChartClientId) || String(local.idClient) === String(selectedCircleChartClientId))
                            : true;
                        })}
                        loading={localsLoading}
                        disabled={localsLoading }
                        isDarkMode={isDarkMode}
                        placeholder="Sélectionner un local..."
                        required={false}
                        className={`w-full p-3 rounded-xl focus:outline-none ${isDarkMode ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'} ${(localsLoading || !selectedCircleChartClientId) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                    </div>
                  </div>
            
              
              </div>
  
              {user?.isAdmin && selectedCircleLocal && (
                <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <strong>Local:</strong> {selectedCircleLocal.adresse}
                    {selectedCircleLocal.responsable && ` - ${selectedCircleLocal.responsable}`}
                  </p>
                </div>
              )}
              

              {!user?.isAdmin && (
                <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <strong>Vos données:</strong> Contrôles réglementaires
                  </p>
                </div>
              )}
              
              {circleChartLoading ? (
                <div className="h-80 flex items-center justify-center">
                  <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`}></div>
                </div>
              ) : circleChartData ? (
                <>
                  <div className="h-80 flex items-center justify-center">
                    <div className="relative w-64 h-64">
                      <Doughnut 
                        data={circleChartData} 
                        options={doughnutOptions}
                        plugins={[centerTextPlugin]}
                      />
                    </div>
                  </div>
                  <div className="mt-6 space-y-2">
                    <div className={`text-center text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <p className="font-medium">{lastIntervention.client?.raisonSocial}</p>
                      <p>Type: {lastIntervention.client?.annuelle ? 'Annuel' : 'Semestriel'}</p>
                      <p>Date: {new Date(lastIntervention.startDate).toLocaleDateString('fr-FR')}</p>
                    </div>
                  </div>
                </>
              ) : (
                <EmptyChartState
                  title="Aucune intervention"
                  icon="fas fa-chart-pie"
                  description={user?.isAdmin && !selectedCircleChartLocalId ? "Sélectionnez un local pour voir les données" : "Aucune intervention trouvée"}
                />
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
